import { useState, useRef, useEffect } from 'react';
import type { AnalyticalResponse, AnalyticalRow } from '@/api/dataVault';
import { SkeletonTable } from '../components/ui/SkeletonLoader';

interface Props {
  data: AnalyticalResponse | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  /** Selected QC rider types (sample_type). Empty = all QC types. */
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  onSelectWell?: (wellId: string) => void;
}

// QC rider types (analytical_results.sample_type, excluding 'N' = field sample).
// Mirrors the model's classification: SUR/FD/FB/TB/LCS/MS/MSD/DUP. Surfaced as a
// "QA/QC" dropdown with checkbox multi-select (see the filter control below).
const QC_TYPES: { code: string; label: string }[] = [
  { code: 'SUR', label: 'Surrogate' },
  { code: 'FD', label: 'Field Dup' },
  { code: 'FB', label: 'Field Blank' },
  { code: 'TB', label: 'Trip Blank' },
  { code: 'LCS', label: 'LCS' },
  { code: 'MS', label: 'Matrix Spike' },
  { code: 'MSD', label: 'MS Dup' },
  { code: 'DUP', label: 'Lab Dup' },
];
const QC_LABEL: Record<string, string> = Object.fromEntries(
  QC_TYPES.map((t) => [t.code, t.label]),
);

function fmtValue(row: AnalyticalRow): string {
  if (row.is_non_detect) {
    return row.detection_limit != null ? `< ${row.detection_limit}` : 'ND';
  }
  return row.concentration != null ? String(row.concentration) : '--';
}

export function QaqcTab({
  data, isLoading, page, onPageChange, selectedTypes, onTypesChange, onSelectWell,
}: Props) {
  const toggleType = (code: string) => {
    if (selectedTypes.includes(code)) {
      onTypesChange(selectedTypes.filter((c) => c !== code));
    } else {
      onTypesChange([...selectedTypes, code]);
    }
  };

  // "QA/QC" labeled dropdown with checkbox multi-select (pattern mirrors
  // DataTableColumnVisibility: button toggles a panel, outside-click closes).
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!filterOpen) return;
    function handleClick(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filterOpen]);

  return (
    <div>
      {/* QC-type filter — labeled "QA/QC" dropdown, checkbox multi-select. */}
      <div className="mb-3">
        <div className="relative inline-block" ref={filterRef}>
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            QA/QC{selectedTypes.length > 0 ? ` (${selectedTypes.length})` : ''}
            <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,4 6,8 10,4" />
            </svg>
          </button>

          {filterOpen && (
            <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] bg-mono-900 border border-mono-700 rounded-lg shadow-xl p-2 space-y-1">
              <button
                type="button"
                onClick={() => onTypesChange([])}
                className="w-full flex items-center gap-2 px-2 py-1 text-xs text-left text-mono-200 hover:text-mono-100 hover:bg-mono-800/50 rounded cursor-pointer select-none"
              >
                <span
                  aria-hidden
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                    selectedTypes.length === 0 ? 'bg-cyan-500 border-cyan-500' : 'bg-mono-800 border-mono-600'
                  }`}
                >
                  {selectedTypes.length === 0 && (
                    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-mono-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="2,6.5 5,9 10,3.5" />
                    </svg>
                  )}
                </span>
                All QC
              </button>
              <div className="border-t border-mono-800 my-1" />
              {QC_TYPES.map((t) => {
                const checked = selectedTypes.includes(t.code);
                return (
                  <button
                    key={t.code}
                    type="button"
                    onClick={() => toggleType(t.code)}
                    className="w-full flex items-center gap-2 px-2 py-1 text-xs text-left text-mono-200 hover:text-mono-100 hover:bg-mono-800/50 rounded cursor-pointer select-none"
                    title={t.code}
                  >
                    <span
                      aria-hidden
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                        checked ? 'bg-cyan-500 border-cyan-500' : 'bg-mono-800 border-mono-600'
                      }`}
                    >
                      {checked && (
                        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-mono-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="2,6.5 5,9 10,3.5" />
                        </svg>
                      )}
                    </span>
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="animate-fade-in"><SkeletonTable rows={8} /></div>
      ) : !data || data.items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-mono-400 text-xs tracking-wider">
            {data?.total === 0 ? 'NO QC DATA MATCHES YOUR FILTERS' : 'SELECT SITES TO VIEW QC DATA'}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3" style={{ borderBottom: '1px solid transparent', borderImage: 'linear-gradient(to right, rgba(6,182,212,0.3), rgba(168,85,247,0.3)) 1' }}>
            <div className="text-xs text-mono-200 tracking-wider pb-2">
              {data.total.toLocaleString()} QC RESULTS
              <span className="ml-2 text-mono-500">(surrogates, blanks, spikes, dups — never in Chemistry)</span>
            </div>
          </div>

          <div className="border border-mono-800 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-mono-800 bg-mono-900/50">
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">QC TYPE</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">SITE</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">LOCATION</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">SAMPLE DATE</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">COMPOUND</th>
                  <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">VALUE</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">UNITS</th>
                  <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">DET. LIMIT</th>
                  <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">RECOVERY %</th>
                  <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">RPD %</th>
                  <th className="px-3 py-2.5 text-center text-xs text-mono-300 tracking-widest font-medium">PASS</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">QUALIFIER</th>
                  <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">NOTES</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-mono-800/50">
                {data.items.map((row) => (
                  <tr key={row.id} className="hover:bg-mono-800/30 transition-colors">
                    <td className="px-3 py-2">
                      <span className="inline-block px-1.5 py-0.5 text-[10px] bg-cyan-900/20 text-cyan-300 rounded tracking-wider" title={row.sample_type || undefined}>
                        {row.sample_type ? (QC_LABEL[row.sample_type] || row.sample_type) : '--'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-mono-100 max-w-[180px] truncate">{row.site_name}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.well_name ? (
                        <button
                          onClick={() => row.well_id && onSelectWell?.(row.well_id)}
                          className="text-mono-200 hover:text-cyan-300 transition-colors"
                        >
                          {row.well_name}
                        </button>
                      ) : <span className="text-mono-500">--</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-mono-200">{row.sample_date || '--'}</td>
                    <td className="px-3 py-2 text-xs text-mono-200 max-w-[200px] truncate">{row.compound}</td>
                    <td className="px-3 py-2 text-xs text-mono-100 text-right tabular-nums">{fmtValue(row)}</td>
                    <td className="px-3 py-2 text-xs text-mono-400">{row.units || '--'}</td>
                    <td className="px-3 py-2 text-xs text-mono-300 text-right tabular-nums">{row.detection_limit ?? '--'}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">
                      {row.qc_recovery_pct != null ? (
                        <span className={row.qc_passed === false ? 'text-red-400' : 'text-mono-200'}>{row.qc_recovery_pct.toFixed(1)}%</span>
                      ) : <span className="text-mono-600">--</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">
                      {row.qc_rpd_pct != null ? (
                        <span className={row.qc_passed === false ? 'text-red-400' : 'text-mono-200'}>{row.qc_rpd_pct.toFixed(1)}%</span>
                      ) : <span className="text-mono-600">--</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-center">
                      {row.qc_passed == null ? <span className="text-mono-600">--</span>
                        : row.qc_passed ? <span className="text-emerald-400">PASS</span>
                        : <span className="text-red-400">FAIL</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-mono-300">{row.qualifier || '--'}</td>
                    <td className="px-3 py-2 text-xs text-mono-400 max-w-[240px] truncate" title={row.notes || undefined}>{row.notes || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-mono-800">
              <span className="text-xs text-mono-200 tracking-wider">
                PAGE {data.page} OF {data.total_pages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  PREV
                </button>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= data.total_pages}
                  className="px-3 py-1 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
